import { useState } from "react";
import type { CompanyKeyDTO, ModelLabDTO } from "../../../shared/types";
import { confirmDialog, Dialog } from "../../components/Dialog";
import { ListRow } from "../../components/List";
import { Badge, Button, ErrorText, Field, inputCls, shortDate } from "../../components/ui";
import { del, put } from "../../lib/api";

/**
 * The Company key row of a lab on the Models page (AIT-119). Only Anthropic's can pay for a run, so only its row has
 * Add, Replace and Remove; with `ANTHROPIC_API_KEY` set on Railway, which wins, it reads so and offers nothing. A key
 * goes in here and never comes back: the page knows only that one is set, by whom and when. A key Anthropic refused
 * reads so, with since when and why (AIT-129): nothing runs on it until it is replaced or removed, or changed on Railway.
 */

/** Add or Replace: what it means in plain words first, then the password field. The shape is checked; no call is made with it. */
function SetKeyDialog({ vendor, replacing, onClose, onChanged }: { vendor: ModelLabDTO["vendor"]; replacing: boolean; onClose: () => void; onChanged: () => void }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (busy || !key.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await put(`/api/models/company-keys/${vendor}`, { key });
      onChanged();
      onClose();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={replacing ? "Replace the company key" : "Add the company key"}
      busy={busy}
      primary={
        <Button variant="primary" disabled={busy || !key.trim()} onClick={save}>
          {busy ? "Saving…" : "Save key"}
        </Button>
      }
    >
      <form
        className="space-y-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <p>Agents with no model list will run on this key and pay per token, from the moment you save it.</p>
        <p className="text-muted">A person with no Claude of their own still waits: this key never pays for a person's run.</p>
        <Field label="Anthropic API key" hint="From console.anthropic.com. It is stored encrypted and never shown again.">
          <input type="password" autoComplete="off" spellCheck={false} data-autofocus className={`${inputCls} font-mono`} placeholder="sk-ant-…" value={key} onChange={(e) => setKey(e.target.value)} />
        </Field>
        <ErrorText error={error} />
      </form>
    </Dialog>
  );
}

export function CompanyKeyRow({ vendor, companyKey, onChanged, onError }: { vendor: ModelLabDTO["vendor"]; companyKey: CompanyKeyDTO; onChanged: () => void; onError: (e: string | null) => void }) {
  const [adding, setAdding] = useState(false);
  const { state, setBy, setAt, refused } = companyKey;
  const refusedBadge = <Badge tone="red">Refused by Anthropic</Badge>;
  const remove = async () => {
    if (!(await confirmDialog({ title: "Remove the company key?", body: "Agents with no model list wait in the queue until there is a key again, and a run going on it stops at its next call. The key is deleted, not kept.", confirm: "Remove key", danger: true }))) return;
    onError(null);
    try {
      await del(`/api/models/company-keys/${vendor}`);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      onChanged();
    }
  };
  return (
    <>
      <ListRow
        title={<span className="font-medium">Company key</span>}
        subtitle="Pays for a run when nothing else does"
        trailing={
          state === "railway" ? (
            refused ? refusedBadge : <Badge tone="emerald">Set on Railway</Badge>
          ) : state === "set" ? (
            <>
              {refused ? refusedBadge : <Badge tone="emerald">Set</Badge>}
              <Button size="sm" onClick={() => setAdding(true)}>Replace</Button>
              <Button size="sm" variant="danger" onClick={remove}>Remove</Button>
            </>
          ) : (
            <>
              <Badge>Not set</Badge>
              <Button size="sm" onClick={() => setAdding(true)}>Add</Button>
            </>
          )
        }
      >
        {state === "railway" && <p className="mt-0.5 text-xs text-faint">Set on Railway, which wins over this page: change it there.</p>}
        {state === "set" && <p className="mt-0.5 text-xs text-faint">Set by {setBy ?? "someone no longer here"} on {shortDate(setAt)}.</p>}
        {refused && (
          <p className="mt-0.5 text-xs text-danger">
            Refused by Anthropic since {shortDate(refused.since)}: {refused.detail}. Agents with no model list wait in the queue
            {state === "railway"
              ? " until ANTHROPIC_API_KEY is changed on Railway. Each redeploy tries it once more."
              : " until the key is replaced or removed. The same key again works once its account has credit."}
          </p>
        )}
      </ListRow>
      {adding && <SetKeyDialog vendor={vendor} replacing={state === "set"} onClose={() => setAdding(false)} onChanged={onChanged} />}
    </>
  );
}
